import {
  MIN_TILE_WIDTH,
  TILE_BOOKING_BG,
  TILE_BOOKING_TEXT,
  TILE_INSET,
  TILE_TRIP_BG,
  TILE_TRIP_TEXT,
} from '../constants';
import { columnPosition, startOfDayMs } from './timeScale';

export type Booking = {
  id: string;
  carId?: string;
  from?: string;
  to?: string;
  status?: string;
  // Actual / estimated return of an ACTIVE rental (trip). The detail drawer shows
  // `tripEnd || backBy` as the return time, so rental tiles end there too — NOT at
  // `to`, which can run past the real return (see buildTilesByCar).
  backBy?: string;
  tripEnd?: string;
  // Booking/trip kind from the API: bookings use 'offline'|'rental'|'service'|
  // 'statusChange'|'express'; trips use 'rental'|'serviceTrip'|'plannedServiceTrip'.
  type?: string;
  // true → this "booking" is a PLANNED STATUS CHANGE (block / release-ready), not a
  // rental. It carries `requestedStatus` and is rendered as a background status BAND
  // (see statusPeriods.ts), NOT a tile — so the tile builder skips it.
  statusChange?: boolean;
  requestedStatus?: string;
  rentalPresentableId?: string;
  bookingPresentableId?: string;
};

export type Tile = {
  bookingId: string;
  label: string;
  endLabel: string;
  x: number; // px from the body's left edge (day 0)
  w: number;
  bg: string;
  textColor: string;
  isBooking: boolean;
  // Active rental that is a SERVICE trip → renders a gear icon before the code.
  isServiceTrip: boolean;
  booking: Booking;
};

const BOOKING_STATUSES = ['confirmed', 'offered'];

export const isBookingStatus = (b: Booking): boolean =>
  BOOKING_STATUSES.includes(b.status || '');

// Service trips (gear icon). Bookings carry type 'service'; the trips API uses
// 'fleetServiceTrip' (and older 'serviceTrip' / 'plannedServiceTrip').
const SERVICE_TYPES = [
  'service',
  'serviceTrip',
  'plannedServiceTrip',
  'fleetServiceTrip',
];

export const isServiceTrip = (b: Booking): boolean =>
  SERVICE_TYPES.includes(b.type || '');

export const getTileLabel = (b: Booking): string =>
  b.requestedStatus || b.rentalPresentableId || b.bookingPresentableId || b.id;

// Build the per-car tile map. `days` is the view-model calendar-day array (its [0] is
// the range start). Tiles are 15-MINUTE-granular and positioned on the active zoom's
// COLUMN scale (`columnsPerDay` columns per day, `columnWidth` px each): a tile spans
// the booking's actual from→to time (start floored / end ceiled to the 15-min grid).
//
// `formatEnd` renders the booking's `to` for the scroll-tracking end label. It is
// injected (not hardcoded here) so the label uses the user's locale + a real
// date+time — the caller builds it from `useDateLocale` (see VehicleTimeline).
export const buildTilesByCar = (
  bookings: Booking[],
  days: Date[],
  columnsPerDay: number,
  columnWidth: number,
  formatEnd: (date: Date) => string
): Map<string, Tile[]> => {
  const map = new Map<string, Tile[]>();
  if (!days.length) return map;
  const rangeStartMs = startOfDayMs(days[0].getTime());
  const totalColumns = days.length * columnsPerDay; // exclusive right edge

  bookings.forEach((b) => {
    if (!b.carId || !b.from || !b.to) return;
    // Planned status changes (blocks / release-ready) are drawn as background bands,
    // not tiles — they're routed to buildStatusPeriodsByCar instead.
    if (b.statusChange === true) return;
    const isBooking = isBookingStatus(b);
    const from = new Date(b.from);
    // Active rentals (trips) end at their real/estimated return (`tripEnd || backBy`),
    // matching the detail drawer's "return time" — `to` can run past that. Future
    // bookings (confirmed/offered) keep their scheduled `to`.
    const endSource = !isBooking ? b.tripEnd || b.backBy || b.to : b.to;
    const to = new Date(endSource);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;

    // Fractional column positions at 15-min resolution (start floored, end ceiled).
    const startPos = columnPosition(
      from.getTime(),
      rangeStartMs,
      columnsPerDay,
      'floor'
    );
    const endPos = columnPosition(
      to.getTime(),
      rangeStartMs,
      columnsPerDay,
      'ceil'
    );
    // Skip bookings entirely outside the rendered range.
    if (endPos <= 0 || startPos >= totalColumns) return;

    const clampedStart = Math.max(0, startPos);
    const clampedEnd = Math.min(totalColumns, endPos);

    const x = clampedStart * columnWidth;
    const right = clampedEnd * columnWidth - TILE_INSET;
    const w = Math.max(MIN_TILE_WIDTH, right - x);

    const tile: Tile = {
      bookingId: b.id,
      label: getTileLabel(b),
      endLabel: formatEnd(to),
      x,
      w,
      bg: isBooking ? TILE_BOOKING_BG : TILE_TRIP_BG,
      textColor: isBooking ? TILE_BOOKING_TEXT : TILE_TRIP_TEXT,
      isBooking,
      isServiceTrip: isServiceTrip(b),
      booking: b,
    };

    const list = map.get(b.carId);
    if (list) list.push(tile);
    else map.set(b.carId, [tile]);
  });

  return map;
};
